import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardBody, Col } from 'reactstrap';

import MessageModal from '../../../shared/components/Modals/MessageModal';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getAllAppointments,
  cancelAppointment,
  removeAppointment,
} from '../../../redux/actions/appointmentAction';
import { getStaffDoctorsByFacility } from '../../../redux/actions/staffActions';
import { useAppointmentsHeaders } from './AppointmentsConstants';
import { useAppointmentLabels } from '../../App/customize/hooks/useAppointmentLabels';
import { useGetFacilityServices } from '../../App/customize/hooks/useGetFacilityServices';
import {
  formatAppointmentTypeTitle,
  useGetAppointmentTypes,
} from '../../App/customize/hooks/useGetAppointmentTypes';
import PaginationComponent from '../../../shared/components/pagination/Pagination';
import Config from '../../../config/app.config';
import HeadingText from '../../../shared/components/form/HeadingText';
import StandardTableToolbar from '../../../shared/components/table-toolbar/StandardTableToolbar';
import TableComponent from '../ParentProfile/Components/TableComponent';
import { useDispatch, useSelector } from 'react-redux';
import { useAppointmentStatusesArchiveModal } from '../../modals/AppointmentStatusesArchive/AppointmentStatusesArchive.hooks';
import {
  APPOINTMENTS_FILTER_SEARCH_THRESHOLD,
  APPOINTMENTS_HISTORY_SCOPE,
  APPOINTMENTS_SCOPE_OPTIONS,
  APPOINTMENTS_SORT_OPTIONS,
  buildAppointmentsTimeFilter,
  getAppointmentsDateShortcuts,
  getAppointmentsFilterCategories,
} from './appointmentsToolbarConfig';

export function Appointments() {
  const updatedHintTimerRef = useRef(null);
  const appointmentsFetchGenerationRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const restoredViewRef = useRef(location.state?.appointmentsView || null);
  const restoredView = restoredViewRef.current;
  const dispatch = useDispatch();
  const labels = useAppointmentLabels();
  const { data: services } = useGetFacilityServices();
  const { data: appointmentTypes = [] } = useGetAppointmentTypes({
    filter: { used: 1 },
  });
  const facilityUtcOffset = useSelector(
    (state) => state.user?.profile?.facility?.utcOffset,
  );
  const doctors = useSelector(
    (state) => state.staff.staffDoctorsByFacility || [],
  );
  const isBoardingOnly = !!services?.isBoardingOnly;

  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(restoredView?.search ?? '');
  const [rows, setRows] = useState([]);
  const [timeFilter, setTimeFilter] = useState(
    restoredView?.timeFilter ?? null,
  );
  const [dateView, setDateView] = useState(
    restoredView?.dateView === 'future' ? 'future' : 'past',
  );
  const [historyScope, setHistoryScope] = useState(
    restoredView?.historyScope === APPOINTMENTS_HISTORY_SCOPE.past
      ? APPOINTMENTS_HISTORY_SCOPE.past
      : APPOINTMENTS_HISTORY_SCOPE.hereNow,
  );
  const [sortOrder, setSortOrder] = useState(
    restoredView?.sortOrder != null
      ? restoredView.sortOrder
      : restoredView?.dateView === 'future'
        ? 1
        : -1,
  );
  const [sortField, setSortField] = useState(
    restoredView?.sortField ?? 'dateTime',
  );
  const [offset, setOffset] = useState(restoredView?.offset ?? 0);
  const [limit, setLimit] = useState(
    restoredView?.limit ?? Config.DEFAULT_PAGINATION_LENGTH,
  );
  const [filter, setFilter] = useState(
    restoredView?.filter && !Array.isArray(restoredView.filter)
      ? restoredView.filter
      : {},
  );
  const [showUpdatedHint, setShowUpdatedHint] = useState(false);
  const [responseBox, setResponseBox] = useState({
    showResponse: false,
    responseType: null,
    responseAlertType: null,
    responseFooter: false,
    message: null,
  });
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [confirmType, setConfirmType] = useState(null);
  const { openAppointmentStatusesArchiveModal } =
    useAppointmentStatusesArchiveModal();

  const futureView = dateView === 'future';
  const headers = useAppointmentsHeaders(futureView);
  const dateShortcuts = getAppointmentsDateShortcuts(futureView);
  const sortOptions = useMemo(
    () =>
      APPOINTMENTS_SORT_OPTIONS.map((option) =>
        option.value === 'dateTime'
          ? { ...option, defaultOrder: futureView ? 1 : -1 }
          : option,
      ),
    [futureView],
  );
  const filterCategories = useMemo(
    () =>
      getAppointmentsFilterCategories({
        appointmentTypes,
        doctors,
        appointmentTypeLabel: 'Type',
      }),
    [appointmentTypes, doctors],
  );

  const uhVariantClass =
  futureView && isBoardingOnly
    ? 'uh-variant--boarding-future'
    : futureView
      ? 'uh-variant--vet-future'
      : isBoardingOnly
        ? 'uh-variant--boarding-past'
        : 'uh-variant--vet-past';

  const hasAdditionalNarrowing =
    !!search?.trim() ||
    !!timeFilter ||
    Object.values(filter || {}).some(
      (value) => Array.isArray(value) && value.length > 0,
    );
  const emptyStateMessage = hasAdditionalNarrowing
    ? 'No matching updates to display'
    : 'No updates to display';

  const getAppointments = useCallback(async () => {
    const fetchGeneration = ++appointmentsFetchGenerationRef.current;
    const { status, typeTitle: type, doctor } = filter || {};
    const apiTimeFilter = buildAppointmentsTimeFilter(
      timeFilter,
      facilityUtcOffset || 0,
    );

    const appointments = await getAllAppointments({
      sort: {
        field: sortField,
        order: +sortOrder,
      },
      search,
      status: status?.length > 0 ? status : null,
      type: type?.length > 0 ? type : null,
      doctor: doctor?.length > 0 ? doctor : null,
      timeFilter: apiTimeFilter,
      dateView,
      ...(futureView
        ? {}
        : {
            historyScope,
          }),
      limit,
      offset,
    });

    if (fetchGeneration !== appointmentsFetchGenerationRef.current) {
      return;
    }

    const nextRows = (appointments.data || []).map((row) => ({
      ...row,
      typeTitle: formatAppointmentTypeTitle(row.typeTitle),
    }));
    setRows(nextRows);
    setTotal(appointments.total);
  }, [
    search,
    filter,
    limit,
    offset,
    sortField,
    sortOrder,
    timeFilter,
    dateView,
    futureView,
    historyScope,
    facilityUtcOffset,
  ]);

  useEffect(() => {
    getAppointments();
  }, [getAppointments]);

  useEffect(() => {
    dispatch(getStaffDoctorsByFacility());
  }, [dispatch]);

  useEffect(() => {
    const onAppointmentsRefresh = () => {
      getAppointments();
    };
    window.addEventListener(
      'loyal-appointments-refresh',
      onAppointmentsRefresh,
    );
    return () => {
      window.removeEventListener(
        'loyal-appointments-refresh',
        onAppointmentsRefresh,
      );
    };
  }, [getAppointments]);

  useEffect(
    () => () => {
      if (updatedHintTimerRef.current) {
        clearTimeout(updatedHintTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (restoredView) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // Consume return snapshot once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    await getAppointments();
    setShowUpdatedHint(true);
    if (updatedHintTimerRef.current) {
      clearTimeout(updatedHintTimerRef.current);
    }
    updatedHintTimerRef.current = setTimeout(() => {
      updatedHintTimerRef.current = null;
      setShowUpdatedHint(false);
    }, 1500);
  };

  const handleActions = (type, data) => {
    if (type === Config.appointmentActions.history) {
      return openAppointmentStatusesArchiveModal({
        appointmentId: data.id,
        typeTitle: data.typeTitle || data.typeOther || '',
        doctor: data.doctor || '',
      });
    }
    if (type === Config.appointmentActions.reschedule) {
      if (data.parentStatus === 'inactive') {
        setResponseBox({
          showResponse: true,
          responseType: 'alert',
          responseAlertType: 'danger',
          responseFooter: true,
          message: `${data.parentName} is inactive. Please activate parent to reschedule appointment.`,
        });
      } else {
        navigate(`/appointments/${data.id}`, {
          state: {
            appointmentsView: {
              dateView,
              historyScope,
              search,
              offset,
              limit,
              filter,
              timeFilter,
              sortField,
              sortOrder,
            },
          },
        });
      }
      return;
    }

    let message = '';
    let nextConfirmType = null;

    if (type === Config.appointmentActions.remove) {
      message = 'Are you sure you want to remove this appointment?';
      nextConfirmType = 'remove';
    } else if (type === Config.appointmentActions.cancel) {
      message = 'Are you sure you want to cancel this appointment?';
      nextConfirmType = 'cancel';
    }

    setSelectedRowId(data);
    setConfirmType(nextConfirmType);
    setResponseBox({
      showResponse: true,
      responseType: 'confirm',
      responseAlertType: null,
      responseFooter: true,
      message,
    });
  };

  const handleDateViewToggle = () => {
    const nextView = dateView === 'future' ? 'past' : 'future';
    setDateView(nextView);
    setSortField('dateTime');
    setSortOrder(nextView === 'future' ? 1 : -1);
    setTimeFilter(null);
    setOffset(0);
  };

  const handleHistoryScopeChange = (nextScope) => {
    setHistoryScope(nextScope);
    setOffset(0);
  };

  const handleSort = ({ field, order }) => {
    setSortField(field ?? sortField);
    setSortOrder(order ?? sortOrder);
    setOffset(0);
  };

  const handleFilter = (filterType, filters) => {
    setOffset(0);
    setFilter((prevFilter) => ({
      ...prevFilter,
      [filterType]: filters,
    }));
  };

  const handleClearAllFilters = () => {
    setFilter({});
    setOffset(0);
  };

  const handleSearchChange = useCallback((nextSearch) => {
    setSearch(nextSearch);
    setOffset(0);
  }, []);

  const handleDateShortcutChange = (nextFilter) => {
    setTimeFilter((current) => (current === nextFilter ? null : nextFilter));
    setOffset(0);
  };

  const onSuccess = async () => {
    if (confirmType === 'cancel') {
      await dispatch(cancelAppointment({ appointmentId: selectedRowId }));
    }
    if (confirmType === 'remove') {
      await dispatch(removeAppointment({ appointmentId: selectedRowId }));
    }
    await getAppointments();
    onClose();
  };

  const onClose = () => {
    setResponseBox({
      showResponse: false,
      responseType: null,
      responseAlertType: null,
      responseFooter: false,
      message: null,
    });
  };

  const handlePageLimit = (newLimit) => {
    setLimit(newLimit);
    setOffset(0);
  };

  const handleGridSort = (field) => {
    if (field === sortField) {
      setSortOrder((prevSortOrder) => -prevSortOrder);
    } else {
      setSortField(field);
    }
    setOffset(0);
  };

  return (
    <>
      <Col md={12} lg={12} className="manage-patient-container admin-container">
        <Card>
         <CardBody
           className={`appointments-container ${uhVariantClass}`}
          >
            <div className="sticky-table">
              <div className="ptb-board-header">
                <div className="page-title-with-action ptb-board-header__title-row">
                  <HeadingText text={labels.appointments} />
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-utility"
                      onClick={handleRefresh}
                    >
                      Refresh
                    </button>
                    {showUpdatedHint ? (
                      <span
                        style={{
                          position: 'absolute',
                          left: '100%',
                          marginLeft: 16,
                          fontSize: '12px',
                          color: '#999',
                          whiteSpace: 'nowrap',
                          pointerEvents: 'none',
                        }}
                      >
                        Updated!
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="ptb-board-header__subtitle-row">
                  <div className="ptb-board-header__subtitle-stack">
                    <div className="ptb-board-header__subtitle">
                      {isBoardingOnly
                        ? 'Updates sent from Photo Update Board'
                        : 'Updates sent from the Update Board'}
                    </div>
                    <div className="ptb-board-header__subtitle">
                      using the Loyal Pet iPad and/or Web app
                    </div>
                  </div>
                </div>
              </div>
<div className="appointments-toolbar-row">
  <StandardTableToolbar
    searchValue={search}
    onSearchChange={handleSearchChange}
    searchPlaceholder="Search"
    scope={futureView ? undefined : historyScope}
    onScopeChange={
      futureView ? undefined : handleHistoryScopeChange
    }
    scopeOptions={
      futureView ? [] : APPOINTMENTS_SCOPE_OPTIONS
    }
    sortField={sortField}
    sortOrder={sortOrder}
    onSortChange={handleSort}
    sortOptions={sortOptions}
    filters={filter}
    onFilterChange={handleFilter}
    onClearAllFilters={handleClearAllFilters}
    filterCategories={filterCategories}
    filterSearchThreshold={
      APPOINTMENTS_FILTER_SEARCH_THRESHOLD
    }
    timeFilter={timeFilter}
    onTimeFilterChange={handleDateShortcutChange}
    dateShortcuts={dateShortcuts}
  />
  <button
    type="button"
    className="btn btn-utility"
    aria-pressed={futureView}
    onClick={handleDateViewToggle}
    style={{
      flex: '0 0 auto',
      width: 'auto',
      marginBottom: 0,
      fontWeight: futureView ? 700 : 400,
      borderColor: futureView ? '#9a9a9a' : undefined,
    }}
  >
    Future
  </button>
</div>
            </div>
            <TableComponent
              stickyTop="60px"
              overflowX={false}
              heads={headers}
              rows={rows}
              sortField={sortField}
              sortOrder={sortOrder}
              handleActions={handleActions}
              handleGridSort={handleGridSort}
              handleGridFilter={handleFilter}
              isBoardingOnly={isBoardingOnly}
              facilityUtcOffset={facilityUtcOffset}
              showActions={futureView}
              updatesColumnMode={!futureView}
              labels={{ ...labels, noAppointments: emptyStateMessage }}
            />

            <PaginationComponent
              handlePageLimit={handlePageLimit}
              nextPage={() => setOffset((prevOffset) => prevOffset + limit)}
              prevPage={() => setOffset((prevOffset) => prevOffset - limit)}
              gotoPage={(page) => setOffset(limit * page)}
              limit={limit}
              offset={offset}
              total={total}
            />
          </CardBody>
        </Card>
      </Col>
      <MessageModal
        show={responseBox.showResponse}
        type={responseBox.responseType}
        alertType={responseBox.responseAlertType}
        footer={responseBox.responseFooter}
        onSuccess={onSuccess}
        onClose={onClose}
        message={responseBox.message}
      />
    </>
  );
}
